import {
  getLeoUuid,
  LeoUUID,
  getString,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class GetInvestigationSummaryRPC implements
  LeoRPC<GetInvestigationSummaryRPC.Request, GetInvestigationSummaryRPC.Response, GetInvestigationSummaryRPC.Errors.Errors>
{
  abstract execute(
    request: GetInvestigationSummaryRPC.Request
  ): Promise<LeoRPCResult<GetInvestigationSummaryRPC.Response, GetInvestigationSummaryRPC.Errors.Errors>>;
}

export namespace GetInvestigationSummaryRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    emailId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetInvestigationSummaryRPCRequest: boolean = true;
  
    readonly emailId: LeoUUID;
  
  
    constructor(
    emailId: LeoUUID
    ) {
      this.emailId = emailId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const emailId = getLeoUuid(dto, "emailId", InvalidRequestEmailIdError);
      return new Request(
        emailId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          emailId: this.emailId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetInvestigationSummaryRPC(emailId='${this.emailId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    summary: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetInvestigationSummaryResponse: boolean = true;
  
    readonly summary: string;
  
  
    constructor(
    summary: string
    ) {
      Response.validateSummary(summary);
      this.summary = summary;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const summary = getString(dto, "summary", InvalidResponseSummaryError);
      return new Response(
        summary
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          summary: this.summary
      };
      return dto;
    }
    private static validateSummary = (summary: string): void => {
      if(summary.trim().length === 0) {
        throw new InvalidResponseSummaryNonEmptyError(
          `Attribute summary cannot be an empty string.`
        );
      }
    }
    
  }
  
  Response.prototype.toString = function toString(): string {
    return `GetInvestigationSummary(summary='${this.summary}')`;
  };
  

  export enum RPCError {
    InvalidEmailId = "INVALID_EMAIL_ID",
    InvestigationSummaryNotAvailable = "INVESTIGATION_SUMMARY_NOT_AVAILABLE"
  }

  export namespace Errors {
    
    export interface InvalidEmailIdDTO {
      code: string;
    }
    
    export class InvalidEmailId extends LeoRPCError<RPCError> {
      readonly isInvalidEmailId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidEmailId;
        super(code);
      }
    
    
      toDTO(): InvalidEmailIdDTO {
        const dto: InvalidEmailIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidEmailId.prototype.toString = function toString(): string {
      return `GetInvestigationSummaryRPC.INVALID_EMAIL_ID()`;
    };
    
    
    export interface InvestigationSummaryNotAvailableDTO {
      code: string;
    }
    
    export class InvestigationSummaryNotAvailable extends LeoRPCError<RPCError> {
      readonly isInvestigationSummaryNotAvailable: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvestigationSummaryNotAvailable;
        super(code);
      }
    
    
      toDTO(): InvestigationSummaryNotAvailableDTO {
        const dto: InvestigationSummaryNotAvailableDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvestigationSummaryNotAvailable.prototype.toString = function toString(): string {
      return `GetInvestigationSummaryRPC.INVESTIGATION_SUMMARY_NOT_AVAILABLE()`;
    };
    

    export type Errors = InvalidEmailId | InvestigationSummaryNotAvailable;
    export type ErrorsDTO = InvalidEmailIdDTO | InvestigationSummaryNotAvailableDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidEmailId:
          return new InvalidEmailId();
        case RPCError.InvestigationSummaryNotAvailable:
          return new InvestigationSummaryNotAvailable();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestEmailIdError extends GetInvestigationSummaryRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailIdError";
  }
}

export class InvalidResponseSummaryError extends GetInvestigationSummaryRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSummaryError";
  }
}

export class InvalidResponseSummaryNonEmptyError extends InvalidResponseSummaryError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSummaryNonEmptyError";
  }
}

export class InvalidResponseInvalidEmailIdError extends GetInvestigationSummaryRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidEmailIdError";
  }
}

export class InvalidResponseInvestigationSummaryNotAvailableError extends GetInvestigationSummaryRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvestigationSummaryNotAvailableError";
  }
}

